import React, {Component, createElement} from 'react';
import PropTypes from 'prop-types';
import PureRenderMixin from 'react-addons-pure-render-mixin';
import classNames from 'classnames';
import {ANY_CHILD} from 'prop-utils';
import ObjectUtils from '@spothero/utils/object';
import Badge from 'badge-component';
import Icon from 'icon-component';

class Button extends Component {
    constructor(props) {
        super(props);

        this._onClick = this._onClick.bind(this);

        this.shouldComponentUpdate = PureRenderMixin.shouldComponentUpdate.bind(this);
    }

    _getColor() {
        const {
            color
        } = this.props;

        switch (color) {
            case 'primary':
                return 'Button';

            case 'secondary':
            case 'secondary-danger':
            case 'tertiary':
                return `Button Button-${color}`;

            default:
                return '';
        }
    }

    _getSize() {
        const {
            size
        } = this.props;

        switch (size) {
            case 'lg':
                return `Button-${size}`;

            case 'med':
            default:
                return '';
        }
    }

    _getWidth() {
        const {
            width
        } = this.props;

        switch (width) {
            case 'narrow':
                return `Button-${width}`;

            case 'normal':
            default:
                return '';
        }
    }

    _onClick(evt) {
        const {
            onClick,
            disabled
        } = this.props;

        if (onClick && !disabled) {
            onClick(evt);
        }
    }

    _renderChildren() {
        const {
            children,
            icon,
            iconPosition,
            badgeLabel,
            badgeColor
        } = this.props;

        if (badgeLabel) {
            return <span>{children} <Badge color={badgeColor}>{badgeLabel}</Badge></span>;
        }

        if (!icon) {
            return children;
        } else {
            if (iconPosition === 'left') {
                return <span><Icon name={icon} /> {children}</span>;
            } else {
                return <span>{children} <Icon name={icon} /></span>;
            }
        }
    }

    _generateDataTestId(dataTestId) {
        if (typeof dataTestId === 'undefined') {
            return 'Button-button-default';
        } else {
            return dataTestId;
        }
    }

    _renderElement(classes) {
        const {
            anchor,
            href,
            type,
            target,
            tabIndex,
            onMouseEnter,
            onMouseLeave,
            onTouchStart,
            title,
            dataTestId
        } = this.props;
        const tag = (anchor) ? 'a' : 'button';
        const elOptions = {
            className: classes,
            onClick: this._onClick,
            "data-testid": this._generateDataTestId(dataTestId),
            tabIndex,
            ...ObjectUtils.getPartialMatchedProps(this.props, 'data-')
        };

        if (onMouseEnter) {
            elOptions.onMouseEnter = onMouseEnter;
        }

        if (onMouseLeave) {
            elOptions.onMouseLeave = onMouseLeave;
        }

        if (onTouchStart) {
            elOptions.onTouchStart = onTouchStart;
        }

        if (title) {
            elOptions.title = title;
        }

        if (anchor) {
            elOptions.href = href;

            if (target) {
                elOptions.target = target;
            }
        } else {
            elOptions.type = type;
        }

        return createElement(tag, elOptions, this._renderChildren());
    }

    render() {
        const {
            className,
            block,
            link,
            disabled,
            active,
            loading,
            icon,
            iconPosition,
            pipeBefore
        } = this.props;
        const colorClass = this._getColor();
        const sizeClass = this._getSize();
        const widthClass = this._getWidth();
        const classes = classNames(
            colorClass,
            sizeClass,
            widthClass,
            {'Button-link': link},
            {'Button-block': block},
            {'Button-active': active},
            {'Button-loading': loading},
            {'Button-icon-left': icon && iconPosition === 'left'},
            {'Button-icon-right': icon && iconPosition === 'right'},
            {'Button-disabled': disabled},
            {'Button-pipe-before': pipeBefore},
            className
        );

        return this._renderElement(classes);
    }
}

Button.propTypes = {
    children: ANY_CHILD,
    className: PropTypes.string,
    type: PropTypes.oneOf(['button', 'submit', 'reset']),
    onClick: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    onTouchStart: PropTypes.func,
    anchor: PropTypes.bool,
    href: PropTypes.string,
    color: PropTypes.oneOf(['', 'primary', 'secondary', 'secondary-danger', 'tertiary']),
    size: PropTypes.oneOf(['med', 'lg']),
    width: PropTypes.oneOf(['normal', 'narrow']),
    block: PropTypes.bool,
    link: PropTypes.bool,
    active: PropTypes.bool,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
    icon: PropTypes.string,
    iconPosition: PropTypes.oneOf(['left', 'right', 'center']),
    badgeLabel: PropTypes.string,
    badgeColor: PropTypes.oneOf(['default', 'inverse', 'danger', 'warning', 'success', 'neutral']),
    pipeBefore: PropTypes.bool,
    target: PropTypes.string,
    title: PropTypes.string,
    tabIndex: PropTypes.number,
    dataTestId: PropTypes.string
};

Button.defaultProps = {
    type: 'button',
    color: '',
    size: 'med',
    width: 'normal',
    iconPosition: 'left',
    pipeBefore: false
};

export default Button;



// WEBPACK FOOTER //
// ./frontend/src/js/components/button/Button.jsx